import React, { useEffect, useMemo, useState } from "react";
import { Loader2, ShieldCheck, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../../lib/auth";

const TABS = [
  { v: "applications", l: "Applications" },
  { v: "roster", l: "Buddy roster" },
];

export default function AdminBuddies() {
  const [tab, setTab] = useState("applications");
  const [apps, setApps] = useState([]);
  const [buddies, setBuddies] = useState([]);
  const [counts, setCounts] = useState({ applications: {}, buddies: {} });
  const [loading, setLoading] = useState(true);
  const [note, setNote] = useState({});

  const refresh = async () => {
    setLoading(true);
    try {
      const [{ data: a }, { data: b }] = await Promise.all([
        api.get("/admin/buddy/applications"),
        api.get("/admin/buddies"),
      ]);
      setApps(a.items || []);
      setBuddies(b.items || []);
      setCounts({ applications: a.counts || {}, buddies: b.counts || {} });
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Failed to load.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const pendingApps = useMemo(() => apps.filter((a) => a.status === "pending"), [apps]);

  const review = async (id, action) => {
    try {
      await api.post(`/admin/buddy/applications/${id}/${action}`, { note: note[id] || "" });
      toast.success(action === "approve" ? "Approved. Buddy is now active." : "Rejected.");
      setNote((n) => ({ ...n, [id]: "" }));
      refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Failed.");
    }
  };

  const setBuddyStatus = async (id, status) => {
    try {
      await api.post(`/admin/buddies/${id}/status`, { status });
      toast.success(`Buddy is now ${status}.`);
      refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Failed.");
    }
  };

  return (
    <div data-testid="admin-buddies-page">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-11 h-11 rounded-full bg-[#1A5FAB]/10 flex items-center justify-center">
          <ShieldCheck className="w-5 h-5 text-[#1A5FAB]" />
        </div>
        <div>
          <h1 className="text-2xl font-extrabold text-[#0B132B]">Buddy programme</h1>
          <p className="text-sm text-[#4B5563]">Approve applications and manage the roster.</p>
        </div>
      </div>

      <div className="flex gap-2 mb-6">
        {TABS.map((t) => (
          <button
            key={t.v}
            data-testid={`admin-buddies-tab-${t.v}`}
            onClick={() => setTab(t.v)}
            className={`px-4 py-2 rounded-full text-sm font-semibold ${
              tab === t.v ? "bg-[#0B132B] text-white" : "bg-white text-[#0B132B] border border-gray-200"
            }`}
          >
            {t.l}
            <span className="ml-2 opacity-70">
              {t.v === "applications" ? counts.applications.pending || 0 : counts.buddies.active || 0}
            </span>
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 text-[#23803F] animate-spin" /></div>
      ) : tab === "applications" ? (
        <div className="space-y-3" data-testid="admin-buddies-app-list">
          {pendingApps.length === 0 ? (
            <div className="bg-white rounded-2xl p-8 border border-gray-100 text-center text-sm text-[#4B5563]">
              No pending applications.
            </div>
          ) : (
            pendingApps.map((a) => (
              <div key={a.id} className="bg-white rounded-2xl border border-gray-100 p-5">
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 rounded-full bg-[#2D9B4E] text-white flex items-center justify-center font-bold">
                    {(a.user_name || a.user_email)[0].toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-[#0B132B]">{a.user_name} <span className="text-xs font-normal text-[#4B5563]">· {a.user_email}</span></div>
                    <div className="text-xs uppercase tracking-wider text-[#4B5563] mt-0.5">
                      {a.applicant_role} · {a.location} · {(a.languages || []).join(", ")}
                    </div>
                    <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                      <Field label="Bio" value={a.bio} />
                      <Field label="Motivation" value={a.motivation} />
                    </div>
                    <div className="mt-3 flex flex-wrap gap-1.5">
                      {(a.support_areas || []).map((s) => (
                        <span key={s} className="text-xs px-2 py-1 rounded-full bg-[#F8F9FB] text-[#0B132B]">{s.replace("_", " ")}</span>
                      ))}
                    </div>
                    <div className="mt-3 flex flex-wrap gap-1.5">
                      {(a.availability || []).map((s) => (
                        <span key={s} className="text-xs px-2 py-1 rounded-full bg-[#F8F9FB] text-[#0B132B]">{s.replace("_", " ")}</span>
                      ))}
                    </div>
                    <input
                      data-testid={`admin-app-note-${a.id}`}
                      value={note[a.id] || ""}
                      onChange={(e) => setNote((n) => ({ ...n, [a.id]: e.target.value }))}
                      placeholder="Add a review note (optional, private)"
                      className="w-full mt-4 px-3 py-2 rounded-xl bg-[#F8F9FB] border border-gray-200 focus:bg-white focus:border-[#2D9B4E] focus:outline-none text-sm"
                    />
                    <div className="mt-3 flex gap-2 justify-end">
                      <button
                        data-testid={`admin-app-reject-${a.id}`}
                        onClick={() => review(a.id, "reject")}
                        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-sm text-red-700 border border-red-200 hover:bg-red-50"
                      >
                        <XCircle className="w-4 h-4" /> Reject
                      </button>
                      <button
                        data-testid={`admin-app-approve-${a.id}`}
                        onClick={() => review(a.id, "approve")}
                        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-sm bg-[#2D9B4E] text-white hover:bg-[#2D9B4E]/90"
                      >
                        <CheckCircle2 className="w-4 h-4" /> Approve
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      ) : (
        <div className="space-y-3" data-testid="admin-buddies-roster-list">
          {buddies.length === 0 ? (
            <div className="bg-white rounded-2xl p-8 border border-gray-100 text-center text-sm text-[#4B5563]">
              No Buddies yet.
            </div>
          ) : (
            buddies.map((b) => (
              <div key={b.id} className="bg-white rounded-2xl border border-gray-100 p-5 flex items-center gap-4">
                <div className="w-11 h-11 rounded-full bg-[#1A5FAB] text-white flex items-center justify-center font-bold">
                  {(b.user_name || b.user_email)[0].toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-[#0B132B]">{b.user_name}</div>
                  <div className="text-xs text-[#4B5563]">{b.applicant_role} · {b.location} · {b.active_connection_count || 0}/{b.capacity || 3} active</div>
                </div>
                <span className={`text-xs px-3 py-1 rounded-full font-semibold uppercase tracking-wider ${
                  b.status === "active" ? "bg-[#2D9B4E]/10 text-[#23803F]" :
                  b.status === "paused" ? "bg-orange-100 text-orange-700" :
                  "bg-gray-100 text-gray-700"
                }`}>{b.status}</span>
                {b.status === "active" ? (
                  <button data-testid={`admin-buddy-pause-${b.id}`} onClick={() => setBuddyStatus(b.id, "paused")} className="text-sm text-orange-700 hover:underline">Pause</button>
                ) : b.status === "paused" ? (
                  <button data-testid={`admin-buddy-activate-${b.id}`} onClick={() => setBuddyStatus(b.id, "active")} className="text-sm text-[#23803F] hover:underline">Activate</button>
                ) : null}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-[#4B5563] font-bold">{label}</div>
      <div className="text-sm text-[#0B132B] mt-1 whitespace-pre-line">{value}</div>
    </div>
  );
}
